import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryApi, suppliersApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import toast from 'react-hot-toast';
import QRCode from "react-qr-code";
import './Inventory.css';
function LotModal({ lot, onClose, onSaved }) {
  // Get today's date in YYYY-MM-DD format (IST)
  const today = new Date().toLocaleDateString('en-CA'); // Returns YYYY-MM-DD in local timezone
  const [form, setForm] = useState({
    lot: lot ? {
      lotNumber: lot.lotNumber, fabricType: lot.fabricType, color: lot.color,
      purchasePricePerUnit: lot.purchasePricePerUnit, sellingPricePerUnit: lot.sellingPricePerUnit || '', unit: lot.unit,
      supplier: lot.supplier || '', supplierPhone: lot.supplierPhone || '', purchasedAt: lot.purchasedAt, notes: lot.notes || '',
    } : { lotNumber: '', fabricType: '', color: '', purchasePricePerUnit: '', sellingPricePerUnit: '', unit: 'meter', supplier: '', supplierPhone: '', purchasedAt: today, notes: '' },
    inventory: { clothType: 'shirt_piece', quantityAvailable: '', lowStockThreshold: 5 },
  });
  const [loading, setLoading] = useState(false);

  const { data: suppliers } = useFetch(() => suppliersApi.getAll(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Custom Validation
    if (!form.lot.lotNumber) {
      return toast.error("Lot Number is required");
    }

    if (!form.lot.fabricType) {
      return toast.error("Fabric Type is required");
    }

    if (!form.lot.color) {
      return toast.error("Color is required");
    }

    if (!form.lot.purchasedAt) {
      return toast.error("Purchase Date is required");
    }

    if (!form.lot.purchasePricePerUnit) {
      return toast.error("Price per unit is required");
    }

    // ✅ Supplier Phone validation (similar to SupplierModal)
    if (form.lot.supplier && !form.lot.supplierPhone) {
      return toast.error("Supplier phone number is required when supplier is selected");
    }

    // Validate phone number format if provided
    if (form.lot.supplierPhone) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(form.lot.supplierPhone.replace(/\s/g, ''))) {
        return toast.error("Please enter a valid 10-digit phone number");
      }
    }

    // ✅ Inventory validation (only when creating)
    if (!lot) {
      if (!form.inventory.quantityAvailable) {
        return toast.error("Quantity is required");
      }
    }

    setLoading(true);
    try {
      if (lot) {
        await inventoryApi.updateLot(lot.id, form.lot);
        toast.success('Lot updated');
      } else {
        await inventoryApi.createLot(form);
        toast.success('Lot created with inventory');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
    setLoading(false);
  };

  const setLot = (k, v) => setForm((f) => ({ ...f, lot: { ...f.lot, [k]: v } }));
  const setInv = (k, v) => setForm((f) => ({ ...f, inventory: { ...f.inventory, [k]: v } }));
  const qrValue = lot
    ? `${window.location.origin}/inventory/${lot.id}`
    : '';
  return (
    <div className="modal-overlay lot-modal-page" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{lot ? 'Edit Lot' : 'New Cloth Lot'}</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Lot Number *</label>
                <input
                  className="form-control"

                  value={form.lot.lotNumber}
                  onChange={(e) => setLot('lotNumber', e.target.value)}
                  placeholder="LOT-2024-001"
                />

                {lot && (
                  <QRCode
                    value={qrValue}
                    size={120}
                  />
                )}
              </div>
              {/* <div className="form-group">
                <label className="form-label">Lot Number *</label>
                <input className="form-control" required value={form.lot.lotNumber} onChange={(e) => setLot('lotNumber', e.target.value)} placeholder="LOT-2024-001" />
              </div> */}
              <div className="form-group">
                <label className="form-label">Fabric Type *</label>
                <input className="form-control" value={form.lot.fabricType} onChange={(e) => setLot('fabricType', e.target.value)} placeholder="Cotton, Silk..." />
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Color *</label>
                <input className="form-control" value={form.lot.color} onChange={(e) => setLot('color', e.target.value)} placeholder="White, Navy Blue..." />
              </div>
              <div className="form-group">
                <label className="form-label">Purchase Date *</label>
                <input type="date" className="form-control" value={form.lot.purchasedAt} onChange={(e) => setLot('purchasedAt', e.target.value)} />
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Purchase Price (₹) *</label>
                <input type="number" className="form-control" step="0.01" min="0" value={form.lot.purchasePricePerUnit} onChange={(e) => setLot('purchasePricePerUnit', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Selling Price (₹)</label>
                <input type="number" className="form-control" step="0.01" min="0" value={form.lot.sellingPricePerUnit} onChange={(e) => setLot('sellingPricePerUnit', e.target.value)} />
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Unit</label>
                <select className="form-control" value={form.lot.unit} onChange={(e) => setLot('unit', e.target.value)}>
                  <option value="meter">Meter</option>
                  <option value="yard">Yard</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Supplier</label>
              <select className="form-control" value={form.lot.supplier} onChange={(e) => {
                const selectedSupplier = suppliers?.suppliers?.find(s => s.name === e.target.value);
                setLot('supplier', e.target.value);
                setLot('supplierPhone', selectedSupplier?.phone || '');
              }}>
                <option value="">Select Supplier</option>
                {suppliers?.suppliers?.map((supplier) => (
                  <option key={supplier.id} value={supplier.name}>{supplier.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Supplier Phone</label>
              <input
                type="tel"
                className="form-control"
                value={form.lot.supplierPhone}
                onChange={(e) => setLot('supplierPhone', e.target.value)}
                placeholder="Enter phone number"
              />
            </div>

            {!lot && (
              <>
                <hr className="divider" />
                <h4 className="text-sm font-semibold text-muted mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Initial Inventory</h4>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Cloth Type *</label>
                    <select className="form-control" value={form.inventory.clothType} onChange={(e) => setInv('clothType', e.target.value)}>
                      <option value="shirt_piece">Shirt Piece</option>
                      <option value="pant_piece">Pant Piece</option>
                      <option value="fabric_roll">Fabric Roll</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quantity *</label>
                    <input type="number" className="form-control" step="0.01" min="0" value={form.inventory.quantityAvailable} onChange={(e) => setInv('quantityAvailable', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Low Stock Alert</label>
                    <input type="number" className="form-control" step="0.01" min="0" value={form.inventory.lowStockThreshold} onChange={(e) => setInv('lowStockThreshold', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (lot ? 'Update Lot' : 'Create Lot')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdjustModal({ inventory, onClose, onSaved }) {
  const [form, setForm] = useState({ type: 'add', quantity: '', reason: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.quantity) {
      return toast.error("Quantity is required");
    }

    if (Number(form.quantity) <= 0) {
      return toast.error("Quantity must be greater than 0");
    }
    setLoading(true);
    try {
      await inventoryApi.adjustStock(inventory.id, form);
      toast.success('Stock adjusted');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay adjust-modal-page" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Adjust Stock</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="alert" style={{ background: 'var(--accent-glow)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,0.2)', marginBottom: '1rem' }}>
              Current Stock: <strong>{Number(inventory.quantityAvailable).toFixed(2)}</strong> ({inventory.clothType?.replace('_', ' ')})
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Adjustment Type</label>
                <select className="form-control" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="add">Add Stock</option>
                  <option value="subtract">Remove Stock</option>
                  <option value="correction">Set to Value</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Quantity *</label>
                <input type="number" className="form-control" step="0.01" min="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Reason</label>
              <textarea className="form-control" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for adjustment..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Adjust Stock'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Inventory() {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchLots = useCallback(() => inventoryApi.getLots({ search, page, limit: 20 }), [search, page]);
  const { data, loading, refetch } = useFetch(fetchLots, [search, page]);
  const lots = data?.lots || data || [];
  const pagination = data?.pagination;

  return (
    <div className="page-container inventory-page">
      <div className="page-header">
        <h1 className="page-title">Cloth Inventory</h1>
        <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('lot'); }}>
          + New Lot
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-input">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input className="form-control" placeholder="Search by lot number, fabric type, color..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper" style={{ borderRadius: 0, border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Lot Number</th>
                  <th>Fabric / Color</th>
                  <th>Type</th>
                  <th>Available (units)</th>
                  <th>Price/Unit</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lots.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon">📦</div><p>No lots found</p></div></td></tr>
                ) : lots.map((lot) => (
                  lot.inventory?.map((inv, idx) => (
                    <tr key={`${lot.id}-${inv.id}`}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={lot.inventory.length} className="mono text-sm font-semibold" style={{ color: 'var(--accent-light)' }}>{lot.lotNumber}</td>
                          <td rowSpan={lot.inventory.length}>
                            <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{lot.fabricType}</div>
                            <div className="text-xs text-muted">{lot.color}</div>
                          </td>
                        </>
                      )}
                      <td><span className="badge badge-pending">{inv.clothType?.replace('_', ' ')}</span></td>
                      <td>
                        <div className="font-semibold" style={{ color: Number(inv.quantityAvailable) <= Number(inv.lowStockThreshold) ? 'var(--danger)' : 'var(--success)' }}>
                          {Number(inv.quantityAvailable).toFixed(2)}
                        </div>
                        <div className="text-xs text-muted">threshold: {inv.lowStockThreshold}</div>
                      </td>
                      {idx === 0 && (
                        <>
                          <td rowSpan={lot.inventory.length} className="font-semibold">₹{Number(lot.purchasePricePerUnit).toLocaleString('en-IN')}/{lot.unit}</td>
                          <td rowSpan={lot.inventory.length} className="text-sm text-muted">{lot.supplier || '—'}</td>
                        </>
                      )}
                      <td>
                        {Number(inv.quantityAvailable) <= Number(inv.lowStockThreshold) ? (
                          <span className="badge badge-cancelled">⚠ Low Stock</span>
                        ) : (
                          <span className="badge badge-ready">In Stock</span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/inventory/${inv.id}`)}>View</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(inv); setModal('adjust'); }}>Adjust</button>
                          {idx === 0 && <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(lot); setModal('lot'); }}>Edit</button>}
                        </div>
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && (
          <div className="pagination">
            <span>Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} lots)</span>
            <div className="pagination-btns">
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {modal === 'lot' && <LotModal lot={selected} onClose={() => setModal(null)} onSaved={refetch} />}
      {modal === 'adjust' && <AdjustModal inventory={selected} onClose={() => setModal(null)} onSaved={refetch} />}
    </div>
  );
}
